/**
 * The channel row: identity (`channelTypeId`, `entityType`, `entityId`) plus the
 * feature flags in force (`pinning`, `mentions`, `attachments`, `maxMessageLength`, …).
 *
 * `ChannelView` reads this before anything else, because the flags are what turn the
 * surface config from "what this screen wants" into "what this channel allows".
 */

import {
  type Channel,
  useGetChannelsByCid,
  useGetChannelsMembersByCid,
} from "./channel-endpoints";

/**
 * A channel's identity and features change on the order of never, so the row is cached
 * long and refetched only by an explicit invalidation. The thread underneath it has its
 * own (short) freshness policy.
 */
const CHANNEL_STALE_TIME_MS = 5 * 60 * 1000;

/**
 * With `metadata`, the same row carries live conversation state — unread counts, the
 * last message, `awaitingTeamReply` — which moves constantly, so it cannot inherit the
 * five minutes the flags are happy with. Matches the inbox list's own policy.
 */
const CHANNEL_METADATA_STALE_TIME_MS = 15 * 1000;

export type UseChannelOptions = {
  /**
   * Also ask for `metadata` — the channel's row as the inbox projects it (its people,
   * home, order, unread state, capabilities).
   *
   * Opt-in because it is not free: the server hydrates the full inbox row for it, which
   * a surface that only needs the feature flags should not pay for. Turn it ON for a
   * surface that renders a conversation header beside the thread, so the header and the
   * thread read ONE query — the two share a cache entry, one request warms both, and a
   * hover prefetch of this key covers the whole screen. Leave it off everywhere else.
   *
   * It changes the query key (`includes` rides in it), so a surface that wants the
   * projection must ask for it everywhere it reads the row, or it will open a second
   * entry against the same channel.
   */
  metadata?: boolean;
};

export function useChannel(cid: string, { metadata }: UseChannelOptions = {}) {
  const builder = useGetChannelsByCid({ cid });

  return (metadata ? builder.query({ includes: "metadata" }) : builder).get({
    enabled: !!cid,
    staleTime: metadata
      ? CHANNEL_METADATA_STALE_TIME_MS
      : CHANNEL_STALE_TIME_MS,
  });
}

/**
 * Everyone currently in the channel, with role, read cursor and unread count. Members
 * who have left are omitted by the server.
 *
 * Not read by `ChannelView` — it exists for the surfaces around it: a "who's here"
 * avatar strip, and the mention suggestion source, which should offer channel members
 * rather than every active user in the account (what the office comment composer does
 * today).
 */
export function useChannelMembers(cid: string, enabled = true) {
  return useGetChannelsMembersByCid({ cid }).get({ enabled: enabled && !!cid });
}

export type { Channel };
